import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAppDispatch } from '../store/hooks';
import { addMessage, setTyping, clearTyping } from '../store/slices/chatSlice';
import { getAccessToken } from '../api/client';
let socket = null;
export function getSocket() {
    if (!socket) {
        socket = io('/chat', {
            auth: { token: getAccessToken() },
            autoConnect: false,
            transports: ['websocket'],
        });
    }
    return socket;
}
export function useSocket(meetingId, onError) {
    const dispatch = useAppDispatch();
    const typingTimers = useRef({});
    const lastMessageIdRef = useRef(0);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    useEffect(() => {
        if (!meetingId)
            return;
        const s = getSocket();
        if (!s.connected)
            s.connect();
        s.emit('chat:join', { meetingId });
        // Re-join room after reconnect + sync missed messages
        const onConnect = () => {
            s.emit('chat:join', { meetingId });
            // Sync messages missed during disconnect
            if (lastMessageIdRef.current > 0) {
                s.emit('chat:sync', { meetingId, lastMessageId: lastMessageIdRef.current });
            }
        };
        const onNewMessage = (msg) => {
            dispatch(addMessage(msg));
            lastMessageIdRef.current = Math.max(lastMessageIdRef.current, msg.id);
        };
        const onTyping = ({ userId }) => {
            dispatch(setTyping({ meetingId, userId }));
            // Auto-clear after 3s
            if (typingTimers.current[userId])
                clearTimeout(typingTimers.current[userId]);
            typingTimers.current[userId] = setTimeout(() => {
                dispatch(clearTyping({ meetingId, userId }));
            }, 3000);
        };
        const onSync = ({ messages: synced }) => {
            if (synced.length > 0) {
                for (const msg of synced) {
                    dispatch(addMessage(msg));
                }
                const maxId = Math.max(...synced.map((m) => m.id));
                lastMessageIdRef.current = Math.max(lastMessageIdRef.current, maxId);
            }
        };
        const onError = ({ message }) => {
            onErrorRef.current?.(message);
        };
        s.on('connect', onConnect);
        s.on('chat:newMessage', onNewMessage);
        s.on('chat:typing', onTyping);
        s.on('chat:sync', onSync);
        s.on('chat:error', onError);
        return () => {
            s.off('connect', onConnect);
            s.off('chat:newMessage', onNewMessage);
            s.off('chat:typing', onTyping);
            s.off('chat:sync', onSync);
            s.off('chat:error', onError);
            Object.values(typingTimers.current).forEach(clearTimeout);
        };
    }, [meetingId]);
    const sendMessage = useCallback((data) => {
        if (!meetingId)
            return;
        getSocket().emit('chat:message', { meetingId, ...data });
    }, [meetingId]);
    const sendTyping = useCallback(() => {
        if (!meetingId)
            return;
        getSocket().emit('chat:typing', { meetingId });
    }, [meetingId]);
    const markRead = useCallback((messageId) => {
        if (!meetingId)
            return;
        getSocket().emit('chat:read', { meetingId, messageId });
    }, [meetingId]);
    const syncMessages = useCallback((lastMessageId) => {
        if (!meetingId)
            return;
        getSocket().emit('chat:sync', { meetingId, lastMessageId });
    }, [meetingId]);
    const setLastMessageId = useCallback((id) => {
        lastMessageIdRef.current = id;
    }, []);
    return { sendMessage, sendTyping, markRead, syncMessages, setLastMessageId };
}
